// @ts-check

/** @file Authenticated app-lifetime ownership of the active project coordinator. */

import {useCallback, useEffect, useSyncExternalStore} from "react"

import {closeProjectReplica, openProjectReplica} from "../database/project-replica.js"
import Project from "../models/project.js"
import {reportProjectSyncStatus} from "../shared/project-sync-reporting.js"
import {syncProjectCatalog} from "./project-catalog-sync.js"
import {projectSyncConnectivity} from "./project-sync-connectivity.js"
import {classifyProjectSyncError, ProjectSyncTransportError} from "./project-sync-error.js"
import {projectSyncStatusStore} from "./project-sync-status.js"

/** @import {OpenProjectReplica} from "../database/project-replica.js" */
/** @import {SyncCoordinatorStatus} from "velocious/build/src/sync/sync-coordinator-types.js" */
/** @import {CurrentUserData} from "../auth/use-current-user.jsx" */
/** @import {ProjectSyncAppState} from "./project-sync-connectivity.js" */
/** @typedef {{projectId: string | null, userId: string}} ProjectSyncSelection */
/** @typedef {{generation: number, projectId: string, replica: OpenProjectReplica, routingEpoch: number, userId: string}} ProjectSyncActive */
/** @typedef {{closeReplica?: typeof closeProjectReplica, connectivity?: typeof projectSyncConnectivity, findProject?: (projectId: string) => Promise<InstanceType<typeof Project> | null>, openReplica?: typeof openProjectReplica, reportStatus?: typeof reportProjectSyncStatus, statusStore?: typeof projectSyncStatusStore, syncCatalog?: typeof syncProjectCatalog}} ProjectSyncLifecycleOptions */

const PROJECT_ROUTE = /^\/projects\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/iu
const TASK_DETAIL_ROUTE = /^\/tasks\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:\/|$)/iu
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu

/** Serial, generation-fenced owner for cached project selection and network lifecycle. */
export class ProjectSyncLifecycle {
  /**
   * Creates one serialized authenticated lifecycle owner.
   * @param {ProjectSyncLifecycleOptions} [options] Injectable application seams.
   */
  constructor(options = {}) {
    const {
      closeReplica = closeProjectReplica,
      connectivity = projectSyncConnectivity,
      findProject = async (projectId) => await Project.findBy({id: projectId}),
      openReplica = openProjectReplica,
      reportStatus = reportProjectSyncStatus,
      statusStore = projectSyncStatusStore,
      syncCatalog = syncProjectCatalog
    } = options

    this.closeReplica = closeReplica
    this.connectivity = connectivity
    this.findProject = findProject
    this.openReplica = openReplica
    this.reportStatus = reportStatus
    this.statusStore = statusStore
    this.syncCatalog = syncCatalog
    this.generation = 0
    this.transition = Promise.resolve()
    /** @type {Set<{abortController: AbortController, promise: Promise<void>}>} */
    this.catalogRefreshes = new Set()
    /** @type {ProjectSyncSelection | null} */
    this.selection = null
    /** @type {ProjectSyncActive | null} */
    this.active = null
    /** @type {Set<() => void>} */
    this.listeners = new Set()
    /** @type {(() => void) | null} */
    this.detachStatus = null
    /** @type {string | null} */
    this.reportFingerprint = null
    this.statusOwner = {retry: async () => await this.retry()}
  }

  /**
   * Selects cached state immediately, then refreshes the catalog out of band.
   * @param {{pathname: string, projectId?: string | null, userId: string | number | null | undefined}} args Authenticated route selection.
   * @returns {Promise<void>} Local selection completion.
   */
  select({pathname, projectId: explicitProjectId, userId}) {
    const generation = ++this.generation
    const projectId = routeProjectId(pathname, explicitProjectId)
    const selection = userId ? {projectId, userId: String(userId)} : null

    this.selection = selection
    if (selection) this.statusStore.activate(this.statusOwner)
    const localSelection = this.enqueue(async () => await this.applySelection({generation, selection}))

    if (selection) {
      /** @type {AbortSignal | undefined} */
      let refreshSignal
      void this.trackCatalogRefresh(async (signal) => {
        refreshSignal = signal
        await localSelection
        await this.refreshCatalog({
          generation,
          selection,
          signal
        })
      }).catch((error) => {
        this.publishSelectionFailure({cancellationRequested: refreshSignal?.aborted === true, error, generation, selection})
      })
    }

    return localSelection
  }

  /** Stops all current ownership and makes every in-flight completion stale. */
  async stop() {
    const generation = ++this.generation

    this.selection = null
    const catalogRefreshes = [...this.catalogRefreshes]
    for (const {abortController} of catalogRefreshes) abortController.abort()
    await Promise.all(catalogRefreshes.map(({promise}) => promise))
    await this.enqueue(async () => {
      try {
        await this.closeActive()
      } finally {
        if (generation === this.generation) this.statusStore.deactivate(this.statusOwner)
      }
    })
  }

  /**
   * Keeps manual retry on the active coordinator's single-flight boundary.
   * @returns {Promise<void>} Retry completion.
   * @rejects {Error} When there is no authenticated owner or retry fails.
   */
  async retry() {
    this.connectivity.retry()
    if (this.active) {
      await this.active.replica.coordinator.retry()
      return
    }
    const selection = this.selection
    if (!selection) throw new Error("Cannot retry project sync without an authenticated selection")
    const generation = this.generation

    await this.trackCatalogRefresh(async (signal) => await this.syncCatalog({signal, userId: selection.userId}))
    await this.enqueue(async () => await this.applySelection({generation, requireRoutingMetadata: true, selection}))
  }

  /**
   * Subscribes to exact active-replica ownership changes.
   * @param {() => void} listener Ownership listener.
   * @returns {() => void} Unsubscribe callback.
   */
  subscribe(listener) {
    this.listeners.add(listener)

    return () => this.listeners.delete(listener)
  }

  /**
   * Returns the active replica only when it owns the requested project.
   * @param {string} projectId Canonical project UUID required by a screen.
   * @returns {OpenProjectReplica | null} Exact active replica or null while unavailable/mismatched.
   */
  getActiveReplica(projectId) {
    return this.active?.projectId === projectId ? this.active.replica : null
  }

  /**
   * Makes every catalog write abortable and drainable by sign-out.
   * @param {(signal: AbortSignal) => Promise<void>} callback Catalog work.
   * @returns {Promise<void>} Caller-visible catalog completion.
   */
  trackCatalogRefresh(callback) {
    const abortController = new AbortController()
    const refresh = callback(abortController.signal)
    const catalogRefresh = {
      abortController,
      promise: refresh.then(() => undefined, () => undefined)
    }

    this.catalogRefreshes.add(catalogRefresh)
    return refresh.finally(() => this.catalogRefreshes.delete(catalogRefresh))
  }

  /**
   * Applies one generation-fenced cached project selection.
   * @param {{generation: number, requireRoutingMetadata?: boolean, selection: ProjectSyncSelection | null}} args Selection work.
   * @returns {Promise<void>} Selection completion.
   * @rejects {ProjectSyncTransportError} When refreshed catalog metadata lacks a valid routing epoch.
   */
  async applySelection({generation, requireRoutingMetadata = false, selection}) {
    if (generation !== this.generation) return
    if (!selection?.projectId) {
      await this.closeActive()
      this.statusStore.deactivate(this.statusOwner)
      if (selection) this.statusStore.activate(this.statusOwner)
      return
    }
    const project = await this.findProject(selection.projectId)
    if (generation !== this.generation) return
    const routingEpoch = project?.routingEpoch()

    if (!Number.isSafeInteger(routingEpoch) || Number(routingEpoch) < 1) {
      await this.closeActive()
      this.statusStore.deactivate(this.statusOwner)
      this.statusStore.activate(this.statusOwner)
      if (requireRoutingMetadata) {
        throw new ProjectSyncTransportError({code: "project-routing-metadata-missing", retryable: true})
      }
      return
    }
    if (this.active?.projectId === selection.projectId && this.active.userId === selection.userId && this.active.routingEpoch === routingEpoch) {
      this.active.generation = generation
      return
    }

    await this.closeActive()
    const replica = await this.openReplica({
      projectId: selection.projectId,
      routingEpoch,
      userId: selection.userId
    })

    if (generation !== this.generation) {
      await this.closeReplica(replica)
      return
    }

    const active = {generation, projectId: selection.projectId, replica, routingEpoch, userId: selection.userId}

    this.active = active
    this.publishActiveChange()
    this.detachStatus = replica.coordinator.subscribe((status) => {
      if (this.active !== active || active.generation !== this.generation) return
      this.statusStore.publish(status)
      this.reportStatusOnce({projectId: selection.projectId, status, userId: selection.userId})
    })
  }

  /**
   * Refreshes routing metadata without delaying the cached selection.
   * @param {{generation: number, selection: ProjectSyncSelection | null, signal: AbortSignal}} args Refresh work.
   * @returns {Promise<void>} Catalog refresh completion.
   */
  async refreshCatalog({generation, selection, signal}) {
    if (!selection || generation !== this.generation) return
    await this.syncCatalog({signal, userId: selection.userId})
    if (!selection.projectId || generation !== this.generation) return
    const project = await this.findProject(selection.projectId)
    const epoch = project?.routingEpoch()

    if (this.active?.projectId === selection.projectId && this.active.userId === selection.userId && this.active.routingEpoch === epoch) return
    await this.enqueue(async () => await this.applySelection({generation, requireRoutingMetadata: true, selection}))
  }

  /**
   * Detaches status and releases the currently active replica.
   * @returns {Promise<void>} Release completion.
   */
  async closeActive() {
    const active = this.active

    this.active = null
    if (active) this.publishActiveChange()
    if (this.detachStatus) this.detachStatus()
    this.detachStatus = null
    this.reportFingerprint = null
    if (active) await this.closeReplica(active.replica)
  }

  /** Notifies active-replica external-store subscribers. */
  publishActiveChange() {
    for (const listener of this.listeners) listener()
  }

  /**
   * Publishes a safe nonblocking selection failure for the current generation.
   * @param {{cancellationRequested?: boolean, error: unknown, generation: number, selection: ProjectSyncSelection | null}} args Failure context.
   */
  publishSelectionFailure({cancellationRequested = false, error, generation, selection}) {
    if (generation !== this.generation || !selection) return
    if (cancellationRequested || isRequestCancellation(error)) return
    const offline = !this.connectivity.isOnline()
    const classified = error instanceof Error ? classifyProjectSyncError(error) : {code: "sync-failed", retryable: false}
    const status = Object.freeze({
      conflicts: Object.freeze([]),
      failure: offline ? null : Object.freeze({attempt: 1, at: new Date().toISOString(), code: classified.code, retryable: classified.retryable}),
      lastSuccessAt: null,
      nextRetryAt: null,
      pendingCount: 0,
      rejectedCount: 0,
      state: offline ? "offline" : "failed"
    })

    this.statusStore.publish(status)
    this.reportStatusOnce({projectId: selection.projectId || "catalog", status, userId: selection.userId})
  }

  /**
   * Reports each safe failed/conflicted snapshot once.
   * @param {{projectId: string, status: SyncCoordinatorStatus, userId: string}} args Safe report context.
   */
  reportStatusOnce({projectId, status, userId}) {
    if (status.state !== "failed" && status.state !== "conflicted") return
    const fingerprint = JSON.stringify({conflicts: status.conflicts, failure: status.failure, projectId, state: status.state, userId})

    if (fingerprint === this.reportFingerprint) return
    this.reportFingerprint = fingerprint
    this.reportStatus({projectId, status, userId})
  }

  /**
   * Serializes lifecycle transitions while preserving each caller's rejection.
   * @template Result - Transition result.
   * @param {() => Promise<Result>} callback Transition callback.
   * @returns {Promise<Result>} Caller-visible transition.
   */
  enqueue(callback) {
    const transition = this.transition.then(callback, callback)

    this.transition = transition.then(() => undefined, () => undefined)

    return transition
  }
}

export const projectSyncLifecycle = new ProjectSyncLifecycle()

/**
 * Returns only the currently active replica that exactly owns a screen's project.
 * @param {string} projectId Canonical project UUID required by the screen.
 * @returns {OpenProjectReplica | null} Exact active replica or null during selection/teardown.
 */
export function useActiveProjectReplica(projectId) {
  const subscribe = useCallback((listener) => projectSyncLifecycle.subscribe(listener), [])
  const getSnapshot = useCallback(() => projectSyncLifecycle.getActiveReplica(projectId), [projectId])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Starts selection from authenticated chrome without introducing a loading gate.
 * @param {{appState: ProjectSyncAppState, pathname: string, projectId?: string | null, user: CurrentUserData | null}} args Authenticated route state.
 * @returns {void}
 */
export function useProjectSyncLifecycle({appState, pathname, projectId, user}) {
  const userId = user?.id ?? null

  useEffect(() => projectSyncConnectivity.bindAppState(appState), [appState])
  useEffect(() => {
    void projectSyncLifecycle.select({pathname, projectId, userId})
  }, [pathname, projectId, userId])
  useEffect(() => () => {
    void projectSyncLifecycle.stop().catch(() => {
      console.warn("Could not stop project sync lifecycle")
    })
  }, [])
}

/**
 * Extracts a canonical project UUID from authenticated routes.
 * @param {string} pathname Current route.
 * @param {string | null | undefined} explicitProjectId Explicit project identity carried by a task-detail route.
 * @returns {string | null} Canonical project UUID.
 * @throws {Error} When an explicit task-detail project id is malformed.
 */
function routeProjectId(pathname, explicitProjectId) {
  const projectRouteId = pathname.match(PROJECT_ROUTE)?.[1]
  if (projectRouteId) return projectRouteId.toLowerCase()
  if (!TASK_DETAIL_ROUTE.test(pathname) || explicitProjectId == null) return null
  if (!UUID_PATTERN.test(explicitProjectId)) throw new Error("Task detail requires a valid project UUID")

  return explicitProjectId.toLowerCase()
}

/**
 * Reports whether a catalog refresh was cancelled instead of failing. A browser
 * navigation aborts the in-flight request, and the rejection arrives as an
 * `AbortError` DOMException — which is not an `Error`, so the classifier would
 * publish the cancellation as a permanent sync failure.
 * @param {unknown} error Rejected value from the catalog refresh.
 * @returns {boolean} Whether the refresh was cancelled by navigation or teardown.
 */
function isRequestCancellation(error) {
  return typeof error === "object" && error !== null && /** @type {{name?: unknown}} */ (error).name === "AbortError"
}
